import { useEffect, useState } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'

import { MentionLastReplies_Mention } from '../../Mentions/MentionCard/MentionLastReplies'
import { MentionData_Mention } from '../../Mentions/fragments/mentionData'
import type { MentionData } from '../../Mentions/fragments/mentionData'

/**
 * The conversations query rejects more than this many ids per request (it
 * matches the inbox page size), so a growing list of replied mentions is
 * fetched in chunks of at most this size. Must stay at or below the backend's
 * per-request limit.
 */
const REPLIES_CHUNK_SIZE = 20

const chunk = (ids: string[], size: number): string[][] => {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size))
  }
  return chunks
}

/**
 * Fetches the replies (`descendants`) below mentions: the inbox list passes
 * every replied mention's id at once rather than each card fetching on its own.
 * It selects only `descendants` — never the `ancestors` thread context shown
 * above a mention, which is loaded separately and lazily — to keep the payload
 * small.
 */
const MentionRepliesBatchQueryGQL = graphql(
  /* GraphQL */ `
    query MentionRepliesBatch($input: EngagementConversationsInput!) {
      engagementConversations(input: $input) {
        conversations {
          engagementId
          descendants {
            __typename
            ... on Mention {
              ...MentionLastReplies_Mention
              ...MentionData_Mention
              id
              parentId
              channelId
              organizationId
              serviceType
              mentionType
              status
              text
              publicUrl
              publishedAt
              isOwnEngagement
              contentType
              suggestions {
                text
                source
              }
              author {
                name
                username
                avatar
                letterAvatar {
                  letter
                  textColor
                  backgroundColor
                }
              }
              assets {
                __typename
                id
                mimeType
                thumbnail
                source
                type
                ... on ImageAsset {
                  image {
                    altText
                    width
                    height
                    isAnimated
                  }
                }
                ... on VideoAsset {
                  video {
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
  [MentionLastReplies_Mention, MentionData_Mention],
)

type BatchData = ResultOf<typeof MentionRepliesBatchQueryGQL>
type ConversationBucket =
  BatchData['engagementConversations']['conversations'][number]

const collectReplies = (
  conversations: ConversationBucket[],
  into: Map<string, MentionData[]>,
): void => {
  for (const conversation of conversations) {
    // Keep only `Mention` items from the polymorphic engagement union.
    const replies = conversation.descendants.flatMap((item) =>
      item.__typename === 'Mention'
        ? [readFragment(MentionData_Mention, item)]
        : [],
    )

    // Omit mentions with no replies so the map only holds real entries (callers
    // default a missing key to an empty list).
    if (replies.length === 0) {
      continue
    }

    // `descendants` arrive newest-first; reverse so the newest reply renders
    // last and the thread reads top-to-bottom in time order.
    into.set(conversation.engagementId, replies.toReversed())
  }
}

/**
 * Fetches replies for the given mentions and returns them keyed by mention id.
 * Each entry is oldest-first (newest reply last), ready to render directly below
 * the main mention; mentions with no replies are absent from the map, so callers
 * default to an empty list.
 *
 * The id list can outgrow the backend's per-request limit as the inbox
 * paginates, so it's split into chunks and fetched in parallel. Each chunk has
 * stable ids, so a cache-first fetch means an already-loaded chunk is a cache
 * hit and only a freshly paginated chunk reaches the network.
 */
export const useMentionRepliesBatch = (
  mentionIds: string[],
): Map<string, MentionData[]> => {
  const client = useApolloClient()
  const [repliesById, setRepliesById] = useState<Map<string, MentionData[]>>(
    () => new Map(),
  )

  // A primitive key so the effect re-runs only when the id set actually
  // changes, not on every render that hands us a fresh array reference.
  const mentionIdsKey = mentionIds.join(',')

  useEffect(() => {
    if (mentionIds.length === 0) {
      setRepliesById(new Map())
      return
    }

    let active = true

    const load = async (): Promise<void> => {
      const results = await Promise.allSettled(
        chunk(mentionIds, REPLIES_CHUNK_SIZE).map((ids) =>
          client.query<
            BatchData,
            VariablesOf<typeof MentionRepliesBatchQueryGQL>
          >({
            query: MentionRepliesBatchQueryGQL,
            variables: {
              input: {
                engagementIds: ids,
                engagementType: 'mention',
              },
            },
            fetchPolicy: 'cache-first',
          }),
        ),
      )

      if (!active) {
        return
      }

      // Merge every chunk that resolved; a single failed chunk drops only its
      // own replies rather than blanking the whole list.
      const next = new Map<string, MentionData[]>()
      for (const result of results) {
        if (result.status !== 'fulfilled') {
          continue
        }
        const conversations =
          result.value.data?.engagementConversations.conversations ?? []
        collectReplies(conversations, next)
      }
      setRepliesById(next)
    }

    void load()

    return (): void => {
      active = false
    }
    // `mentionIdsKey` is the stable projection of `mentionIds` used above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionIdsKey, client])

  return repliesById
}
